import Vue from 'vue';
import Router from 'vue-router';
import Home from './views/Home.vue';
import Login from '@/views/Login.vue';
import DefaultContainer from '@/views/DefaultContainer.vue';
import PageNotFound from '@/views/404.vue';
import AssetManager from '@/views/AssetManager.vue';
import Users from '@/views/Users.vue';
import CreateEditDashboard from '@/views/CreateEditDashboard.vue';
import DashboardDisplay from '@/views/DashboardDisplay.vue';
import DeviceManager from '@/views/DeviceManager.vue';
import Asset from '@/views/Asset.vue';
import Device from '@/views/Device.vue';
import Tenants from '@/views/Tenants.vue';
import AlertHistory from '@/views/AlertHistory.vue';
import MyProfile from '@/views/me/MyProfile.vue';
import AlertRuleList from '@/views/AlertRuleList.vue';
import AlertRuleCreateUpdate from '@/views/AlertRuleCreateUpdate.vue';

Vue.use(Router);

export const createRouter = (kuzzle: any, store: any) => {
  const authenticationGuard = async (to: any, from: any, next: any) => {
    try {
      if (await store.dispatch('auth/CHECK_TOKEN', kuzzle)) {
        next();
      } else {
        next({ name: 'login', query: { to: to.name } });
      }
    } catch (error) {
      console.error(error.message);
      next({ name: 'login', query: { to: to.name } });
    }
  };

  const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes: [
      {
        path: '/login',
        name: 'login',
        component: Login
      },
      {
        path: '/',
        beforeEnter: authenticationGuard,
        component: DefaultContainer,
        children: [
          {
            path: '/',
            name: 'dashboard',
            component: Home
          },
          {
            path: '/dashboard/:dashboardId',
            name: 'dashboard-page',
            component: DashboardDisplay
          },
          {
            path: '/dashboards/create',
            name: 'new-dashboard',
            component: CreateEditDashboard
          },
          {
            path: '/dashboards/edit/:dashboardId',
            name: 'edit-dashboard',
            component: CreateEditDashboard,
            props: true
          },
          {
            path: '/assets',
            name: 'assets',
            component: AssetManager
          },
          {
            path: '/asset/:assetId',
            name: 'asset',
            component: Asset
          },
          {
            path: '/devices',
            name: 'devices',
            component: DeviceManager
          },
          {
            path: '/device/:deviceId',
            name: 'device',
            component: Device
          },
          {
            path: '/alerts',
            name: 'alerts-history',
            component: AlertHistory
          },
          {
            path: '/admin/users',
            name: 'users-management',
            component: Users
          },
          {
            path: '/admin/alert-rules',
            name: 'alert-rule-list',
            component: AlertRuleList
          },
          {
            path: '/admin/alert-rules/new/',
            name: 'alert-rule-create',
            component: AlertRuleCreateUpdate,
            props: () => ({
              ruleId: null
            })
          },
          {
            path: '/admin/alert-rules/update/:id',
            name: 'alert-rule-update',
            component: AlertRuleCreateUpdate,
            props: route => ({
              ruleId: route.params.id
            })
          },
          {
            path: '/platform-management/tenants',
            name: 'tenants-management',
            component: Tenants
          },
          {
            path: '/me/profile',
            name: 'My Profile',
            component: MyProfile
          }
        ]
      },
      {
        path: '*',
        component: PageNotFound
      }
    ]
  });

  router.beforeEach(async (to, from, next) => {
    /**
     * This route guard ensures that the app is connected to Kuzzle before
     * passing to the next route.
     */
    const offlinePageDelay = 1500;
    return new Promise(async resolve => {
      setTimeout(() => {
        if (!store.state.app.online) {
          store.commit('app/SET_WAITING_FOR_CONNECTION');
        }
      }, offlinePageDelay);
      const onceConnected = () => {
        store.commit('app/SET_ONLINE');
        setTimeout(() => {
          store.commit('app/UNSET_WAITING_FOR_CONNECTION');
        }, offlinePageDelay);

        kuzzle.removeListener('connected', onceConnected);
        kuzzle.removeListener('reconnected', onceConnected);

        next();
        return resolve(null);
      };
      if (!store.state.app.online) {
        kuzzle.addListener('connected', onceConnected);
        kuzzle.addListener('reconnected', onceConnected);
      } else {
        onceConnected();
      }
    });
  });

  return router;
};
